import type { ReactNode } from 'react';

/**
 * Autocomplete option for search suggestions
 */
export interface AutocompleteOption {
  /** Display label for the option */
  label: string;
  /** Value to set when selected */
  value: string;
  /** Optional prefix element (icon, badge, etc.) */
  prefix?: ReactNode;
  /** Category for grouping (product, sku, etc.) */
  category?: 'product' | 'sku';
}

/**
 * Filter configuration for Polaris Filters component
 */
export interface FilterConfig {
  key: string;
  label: string;
  filter: ReactNode;
  shortcut?: boolean;
  pinned?: boolean;
  disabled?: boolean;
}

/**
 * Applied filter for display badges
 */
export interface AppliedFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

/**
 * Range filter values (min/max)
 */
export interface RangeFilterValue {
  min: string;
  max: string;
}

/**
 * URL filter state structure
 */
export interface URLFilterState {
  q?: string;
  severity?: string[];
  daysMin?: string;
  daysMax?: string;
  inventoryMin?: string;
  inventoryMax?: string;
  tab?: string;
}

/**
 * Parse URL search params to filter state
 */
export function parseURLFilters(searchParams: URLSearchParams): URLFilterState {
  return {
    q: searchParams.get('q') || undefined,
    severity: searchParams.getAll('severity').filter(Boolean),
    daysMin: searchParams.get('daysMin') || undefined,
    daysMax: searchParams.get('daysMax') || undefined,
    inventoryMin: searchParams.get('inventoryMin') || undefined,
    inventoryMax: searchParams.get('inventoryMax') || undefined,
    tab: searchParams.get('tab') || undefined,
  };
}

/**
 * Serialize filter state to URL search params
 * Preserves existing Shopify params (shop, host, embedded, etc.)
 */
export function serializeFiltersToURL(
  filters: URLFilterState,
  existingParams: URLSearchParams
): URLSearchParams {
  const params = new URLSearchParams();

  // Preserve Shopify params
  const shopifyParams = ['shop', 'host', 'embedded', 'hmac', 'locale', 'session', 'timestamp'];
  shopifyParams.forEach((key) => {
    const value = existingParams.get(key);
    if (value) {
      params.set(key, value);
    }
  });

  // Add filter params
  if (filters.q) params.set('q', filters.q);
  if (filters.severity && filters.severity.length > 0) {
    filters.severity.forEach((s) => params.append('severity', s));
  }
  if (filters.daysMin) params.set('daysMin', filters.daysMin);
  if (filters.daysMax) params.set('daysMax', filters.daysMax);
  if (filters.inventoryMin) params.set('inventoryMin', filters.inventoryMin);
  if (filters.inventoryMax) params.set('inventoryMax', filters.inventoryMax);
  if (filters.tab) params.set('tab', filters.tab);

  return params;
}

/**
 * Format range for display in applied filter badge
 */
export function formatRangeLabel(
  min: string | undefined,
  max: string | undefined,
  unit: string = ''
): string {
  const unitSuffix = unit ? ` ${unit}` : '';

  if (min && max) {
    return `${min}-${max}${unitSuffix}`;
  }
  if (min && !max) {
    return `${min}+${unitSuffix}`;
  }
  if (!min && max) {
    return `0-${max}${unitSuffix}`;
  }
  return '';
}
